import { ChangeEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Box, Flex, Image, Switch, Text } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@/components/AutoResizeTextarea';
import { Textarea } from '@/components/lib/Textarea';
import { useAppSelector } from '@/store/hooks';

interface NoteTextareaProps {
  profilePic: string;
  noteContent: string;
  setNoteContent: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onSaveNote: () => void;
  isNotePublic: boolean;
  setIsNotePublic: (isPublic: boolean) => void;
  isResizable?: boolean;
  hideAvatar?: boolean;
}

const NoteTextarea = ({
  profilePic,
  noteContent,
  setNoteContent,
  onSaveNote,
  isNotePublic,
  setIsNotePublic,
  isResizable = true,
  hideAvatar = false,
}: NoteTextareaProps) => {
  const navigate = useNavigate();
  const authState = useAppSelector((state) => state.auth);

  const handleClickAvatar = () => {
    navigate({ to: `/profile/${authState.uniqId}` });
  };

  return (
    <Flex
      alignItems="flex-start"
      flexDirection="column"
      mb={2}
      mt={4}
      bg="white"
      borderRadius="45px"
      p={4}
      _hover={{
        boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-1px)',
      }}
    >
      <Flex align="flex-start" gap={4} width="100%">
        {!hideAvatar && (
          <Image
            src={profilePic}
            alt="Your profile picture"
            borderRadius="full"
            objectFit="cover"
            boxSize="52px"
            flexShrink={0}
            onClick={handleClickAvatar}
            cursor="pointer"
          />
        )}
        <Box flex={1} position="relative">
          {isResizable ? (
            <AutoResizeTextarea
              placeholder="Share something about yourself..."
              value={noteContent}
              onChange={setNoteContent}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  onSaveNote();
                }
              }}
              maxLength={2000}
              minHeight="192px"
              width="100%"
              fontSize="md"
              fontWeight="bold"
              color="black"
              bg="transparent"
              border="none"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              pl={0}
            />
          ) : (
            <Textarea
              placeholder="Share something about yourself..."
              value={noteContent}
              onChange={setNoteContent}
              resize="none"
              overflow="hidden"
              w="100%"
              fontSize="md"
              fontWeight="bold"
              minHeight="192px"
              border="none"
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
            />
          )}
        </Box>
      </Flex>
      <Flex alignItems="center" width="100%" justifyContent="space-between" bg="white" px={2} borderRadius="full">
        <Flex align="center" gap={2}>
          <Switch
            size="lg"
            sx={{
              '& .chakra-switch__track[data-checked]': {
                backgroundColor: '#ff0050',
              },
            }}
            onChange={(e) => {
              setIsNotePublic(!e.target.checked);
            }}
            isChecked={!isNotePublic}
          />
          <Text color="black" fontSize="md" fontWeight="bold">
            Private
          </Text>
        </Flex>

        <Text fontSize="sm" color="gray.500">
          {noteContent.length}/2000
        </Text>
      </Flex>
    </Flex>
  );
};

export default NoteTextarea;
